import { Link, useNavigate } from 'react-router-dom'
import AppActionMenu from '../../../components/AppActionMenu'
import AppDateInput from '../../../components/AppDateInput'
import AppPagination from '../../../components/AppPagination'
import AppDropdown from '../../../components/AppDropdown'
import { useBranchListScreen } from './hooks/useBranchListScreen'
import MasterApprovalStatusBadge from '../../approval/components/MasterApprovalStatusBadge'



export default function BranchMasterListScreen() {
  const { state, actions } = useBranchListScreen()
  const navigate = useNavigate()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-8 pb-8">
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <h1 className="text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">Branch Master</h1>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            Manage your global organizational hierarchy. Configure branch-level settings, reporting lines, and
            operational status from a single structural hub.
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-surface-container-low p-1.5">
          <button
            type="button"
            onClick={() => actions.setViewMode('active')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
              state.viewMode === 'active'
                ? 'bg-surface-container-lowest text-primary shadow-sm'
                : 'text-on-surface-variant hover:text-primary'
            }`}
          >
            Active Views
          </button>
          <button
            type="button"
            onClick={() => actions.setViewMode('pending')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
              state.viewMode === 'pending'
                ? 'bg-surface-container-lowest text-primary shadow-sm'
                : 'text-on-surface-variant hover:text-primary'
            }`}
          >
            Pending Review
          </button>
          <button
            type="button"
            onClick={() => actions.setViewMode('archived')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
              state.viewMode === 'archived'
                ? 'bg-surface-container-lowest text-primary shadow-sm'
                : 'text-on-surface-variant hover:text-primary'
            }`}
          >
            Archived
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4 md:items-end">
        <label className="grid gap-2">
          <span className="ml-1 text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Branch Name</span>
          <div className="relative">
            <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
              search
            </span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search branch..."
              className="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3 pl-11 text-sm text-on-surface outline-none"
            />
          </div>
        </label>

        <label className="grid gap-2">
          <span className="ml-1 text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Parent Company</span>
          <AppDropdown
            value={state.companyId}
            onChange={actions.setCompanyId}
            placeholder="All Companies"
            triggerClassName="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3 text-sm text-on-surface"
            options={[
              { value: '', label: 'All Companies' },
              ...state.companyOptions.map((option) => ({
                value: String(option.id),
                label: option.name,
              })),
            ]}
          />
        </label>

        <div className="grid gap-2">
          <span className="ml-1 text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Date Range</span>
          <div className="grid grid-cols-2 gap-2">
            <AppDateInput
              value={state.fromDate}
              onChange={actions.setFromDate}
              inputClassName="w-full rounded-xl border-0 bg-surface-container-low px-3 py-3 text-sm text-on-surface shadow-none"
            />
            <AppDateInput
              value={state.toDate}
              onChange={actions.setToDate}
              inputClassName="w-full rounded-xl border-0 bg-surface-container-low px-3 py-3 text-sm text-on-surface shadow-none"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={actions.resetFilters}
          className="rounded-xl bg-surface-variant px-6 py-3 text-sm font-bold text-on-surface-variant transition-colors hover:bg-outline-variant/60"
        >
          Reset Filters
        </button>
      </section>

      {state.error ? (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="overflow-hidden rounded-[2rem] bg-surface-container-low">
        <div className="grid grid-cols-12 px-8 py-4 text-[11px] font-black uppercase tracking-[0.14em] text-outline/90">
          <div className="col-span-3">Branch Details</div>
          <div className="col-span-3">Parent Company</div>
          <div className="col-span-2">Email / Phone</div>
          <div className="col-span-2">Created Date</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        <div className="space-y-3 p-4">
          {state.isLoading ? (
            <div className="rounded-xl bg-surface-container-lowest px-6 py-10 text-center text-sm text-on-surface-variant">
              Loading branches...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="rounded-xl bg-surface-container-lowest px-6 py-10 text-center text-sm text-on-surface-variant">
              No branches found for selected filters.
            </div>
          ) : (
            state.rows.map((row) => (
              <article
                key={row.id}
                className="grid grid-cols-12 items-center rounded-xl bg-surface-container-lowest px-6 py-5 shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <div className="col-span-3 flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#dae2ff]/40 text-primary">
                    <span className="material-symbols-outlined">location_on</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold text-on-surface">{row.name}</p>
                      {row.isPending && <MasterApprovalStatusBadge isPending={true} />}
                    </div>
                    <p className="text-sm text-on-surface-variant">ID: {row.branchCode}</p>
                  </div>
                </div>

                <div className="col-span-3">
                  <p className="text-base font-semibold text-secondary">{row.companyName}</p>
                  <p className="text-[11px] text-outline">{row.regionLabel}</p>
                </div>

                <div className="col-span-2">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-on-surface">{row.email}</p>
                    <p className="text-xs text-on-surface-variant">{row.phone}</p>
                  </div>
                </div>

                <div className="col-span-2">
                  <p className="text-sm text-on-surface-variant font-medium">{row.createdDate}</p>
                </div>

                <div className="col-span-2 flex items-center justify-end gap-2">
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-2 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      { key: 'archive', label: 'Archive', icon: 'archive' },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/masters/branch/entry?id=${row.id}`);
                      }
                    }}
                  />
                </div>
              </article>
            ))
          )}
        </div>

        <div className="flex items-center justify-between bg-surface-container px-8 py-6">
          <span className="text-sm text-on-surface-variant">{state.rangeLabel}</span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 pt-2 md:grid-cols-3">
        <article className="rounded-2xl border border-outline-variant/20 bg-white/40 p-8 backdrop-blur-sm shadow-sm">
          <span className="material-symbols-outlined mb-4 block text-primary">verified_user</span>
          <h3 className="text-xl font-bold text-on-surface">High Trust Security</h3>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            All branch access protocols are encrypted with AES-256 standard and verified via hardware keys.
          </p>
        </article>

        <article className="rounded-2xl border border-outline-variant/20 bg-white/40 p-8 backdrop-blur-sm shadow-sm">
          <span className="material-symbols-outlined mb-4 block text-primary">hub</span>
          <h3 className="text-xl font-bold text-on-surface">Network Topology</h3>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            Visualization of reporting lines and operational dependencies is available in the Network view.
          </p>
        </article>

        <article className="rounded-2xl border border-outline-variant/20 bg-white/40 p-8 backdrop-blur-sm shadow-sm">
          <span className="material-symbols-outlined mb-4 block text-primary">sync</span>
          <h3 className="text-xl font-bold text-on-surface">Real-time Sync</h3>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            Status updates propagate across all nodes within 400ms ensuring absolute data integrity.
          </p>
        </article>
      </section>
    </div>
  )
}


